import Add16 from '@carbon/icons-react/es/add/16';
import Search16 from '@carbon/icons-react/es/search/16';
import { useBoolean } from 'ahooks';
import { TooltipPlacement } from 'antd/es/tooltip';
import React, {
  FC,
  memo,
  ReactElement,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import StateManager, { components as SelectComponents } from 'react-select';
import styled from 'styled-components';
import { Avatar } from 'components/atoms/avatar';
import { Button } from 'components/atoms/button';
import { OutsideClickHandler } from 'components/atoms/outside-click-handler';
import { Popover } from 'components/atoms/popover';
import { Control, Select } from 'components/atoms/select';
import {
  ListItem,
  ListItemMeta,
} from 'components/atoms/select/custom-components';
import { Option, OptionProps } from 'components/atoms/select/types';
import { Caption01 } from 'components/styles';
import { isChildNode } from 'utils/is-child-node';
import { queueMacrotask } from 'utils/macrotask';
import { UserOption } from 'utils/project-fields/types';

const StyledPopover = styled(Popover)`
  &.ant-popover {
    width: 296px;
    min-height: 448px;
  }
  .ant-popover-content,
  .ant-popover-inner,
  .ant-popover-inner-content {
    height: 100%;
  }
  .ant-popover-inner-content,
  .ozone-select-container {
    display: flex;
    flex-flow: column;
  }
  .ozone-select-container {
    flex: 1;
    overflow: hidden;
    margin-bottom: var(--spacing-05);
  }
  .ozone-select__value-container {
    min-height: 40px;
    max-height: 76px;
    overflow: hidden;
    overflow-y: auto;
    margin: var(--spacing-01);
  }

  .select-done-btn {
    margin-bottom: var(--spacing-03);
    margin-left: var(--spacing-03);
    margin-right: var(--spacing-03);
    justify-content: center;
  }
`;
const SelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const DropdownIndicator = (props) => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      <Search16 />
    </SelectComponents.DropdownIndicator>
  );
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    margin: 'var(--spacing-05)',
  }),
  menu: () => ({ boxShadow: 'none' }),
};
const StyledListItemMeta = styled(ListItemMeta)`
  .ant-list-item-meta-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    .ant-list-item-meta-description {
      ${Caption01};
      color: var(--gray-50);
    }
  }
`;

const PartnerOption = memo((props: OptionProps): JSX.Element => {
  const { data, isSelected, isFocused } = props;

  return (
    <SelectComponents.Option {...props}>
      <ListItem>
        <StyledListItemMeta
          isFocused={isFocused}
          isSelected={isSelected}
          avatar={
            <Avatar
              name={data.label}
              src={data.imageUrl}
              companyLogoUrl={data.logoUrl}
            />
          }
          title={data.label}
          description={data.description}
        />
      </ListItem>
    </SelectComponents.Option>
  );
});

const DefaultTrigger = (
  <Button type="link" size="small" icon={<Add16 />} iconPlacement="right">
    <FormattedMessage id="projects.create.team_details.add_partner" />
  </Button>
);

interface Props {
  options: Option[];
  onChange: (value: UserOption[]) => void;
  selected?: UserOption[];
  className?: string;
  triggerComponent?: ReactElement;
  placement?: TooltipPlacement;
}

export const PartnerSelect: FC<Props> = ({
  options,
  selected,
  onChange,
  className,
  triggerComponent = DefaultTrigger,
  placement = 'bottomLeft',
}) => {
  const [visible, { setTrue: showPopover, setFalse: hidePopover }] =
    useBoolean(false);
  const filterOptions = useMemo(() => {
    const selectedIds = (selected ?? []).map(({ value: id }) => id);

    return options.filter(({ value: id }) => !selectedIds.includes(id));
  }, [options, selected]);
  const [value, setValue] = useState([]);
  const selectRef = useRef<StateManager>();
  const handleChange = useCallback((selectedOptions) => {
    setValue(selectedOptions);
    queueMacrotask(() => {
      selectRef.current?.blur();
      selectRef.current?.focus();
    });
  }, []);
  const isChild = isChildNode([
    'partner-selection',
    'ozone-select__multi-value__remove',
    'ozone-select__option',
  ]);
  const onDone = useCallback(() => {
    if (value != null && value.length > 0) {
      onChange(value);
    }
    hidePopover();
  }, [hidePopover, onChange, value]);
  const onOutsideClick = useCallback(() => {
    if (!visible) {
      return;
    }
    hidePopover();
  }, [hidePopover, visible]);
  const onVisibilityChange = useCallback(() => {
    setValue([]);
    if (visible) {
      onDone();
    } else {
      showPopover();
    }
  }, [onDone, showPopover, visible]);

  return (
    <OutsideClickHandler allowed={isChild} onOutsideClick={onOutsideClick}>
      <StyledPopover
        className={className}
        placement={placement}
        trigger={['click']}
        visible={visible}
        onVisibleChange={onVisibilityChange}
        destroyTooltipOnHide
        content={
          <>
            <SelectContainer>
              <Select
                ref={selectRef}
                type="user"
                isMulti
                menuIsOpen
                autoFocus
                className="partner-selection"
                hideSelectedOptions={false}
                components={{
                  DropdownIndicator,
                  IndicatorSeparator: () => null,
                  ClearIndicator: null,
                  Option: PartnerOption,
                  Control,
                }}
                styles={selectStyles}
                placeholder={<FormattedMessage id="placeholder.search" />}
                options={filterOptions}
                value={value}
                onChange={handleChange}
              />
            </SelectContainer>
            <Button
              type="tertiary"
              className="select-done-btn"
              onClick={onDone}
            >
              <FormattedMessage id="done" />
            </Button>
          </>
        }
      >
        <span>{triggerComponent}</span>
      </StyledPopover>
    </OutsideClickHandler>
  );
};
